import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchPersonas } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'

interface JournalEntry { filename: string; title?: string }

export default function JournalViewerPage() {
  const { persona: paramPersona } = useParams<{ persona: string }>()
  const [selectedPersona, setSelectedPersona] = useState(paramPersona || '')
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null)

  const { data: personas } = useQuery({
    queryKey: ['personas'],
    queryFn: fetchPersonas,
  })

  const { data: entries } = useQuery({
    queryKey: ['journals', selectedPersona],
    queryFn: async () => {
      const res = await fetch(`/api/journals/${selectedPersona}`)
      if (!res.ok) return []
      const data = await res.json()
      return (data.entries || data) as JournalEntry[]
    },
    enabled: !!selectedPersona,
  })

  const { data: entryContent } = useQuery({
    queryKey: ['journalEntry', selectedPersona, selectedEntry],
    queryFn: async () => {
      const res = await fetch(`/api/journals/${selectedPersona}/${encodeURIComponent(selectedEntry!)}`)
      if (!res.ok) return ''
      const data = await res.json()
      return data.content || ''
    },
    enabled: !!selectedPersona && !!selectedEntry,
  })

  const persistentPersonas = (personas || []).filter(p => p.type === 'persistent')

  return (
    <div className="flex h-full">
      {/* Persona + entry sidebar */}
      <div className="w-72 shrink-0 overflow-y-auto py-4 px-2" style={{ borderRight: '1px solid var(--border)' }}>
        <h1 className="text-sm font-semibold mb-3 px-2" style={{ color: 'var(--text-primary)' }}>Journals</h1>

        {/* Persona selector */}
        <div className="flex flex-wrap gap-1 mb-3 px-2">
          {persistentPersonas.map(p => (
            <button
              key={p.name}
              onClick={() => { setSelectedPersona(p.name); setSelectedEntry(null) }}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors"
              style={{
                background: selectedPersona === p.name ? 'var(--bg-elevated)' : 'transparent',
                color: selectedPersona === p.name ? 'var(--text-primary)' : 'var(--text-muted)',
                border: `1px solid ${selectedPersona === p.name ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >
              <PersonaAvatar name={p.name} size={14} />
              {p.name}
            </button>
          ))}
        </div>

        {/* Entry list */}
        {selectedPersona && (entries || []).map(e => (
          <button
            key={e.filename}
            onClick={() => setSelectedEntry(e.filename)}
            className="w-full text-left px-3 py-2 rounded-md text-xs mb-0.5 transition-colors truncate"
            style={{
              background: selectedEntry === e.filename ? 'var(--bg-elevated)' : 'transparent',
              color: selectedEntry === e.filename ? 'var(--text-primary)' : 'var(--text-secondary)',
            }}
          >
            {e.filename.replace('.md', '')}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {entryContent ? (
          <div className="max-w-3xl">
            <MarkdownRenderer content={entryContent} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {selectedPersona ? 'Select a journal entry' : 'Select a persona'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
